/**
 * DTO → view model for My Sessions (HS-1440).
 *
 * Permanent: it survives the removal of `src/tmp/`, because what changes when the endpoints land
 * is where the DTO comes from, not what it means. Every defensive branch here is about a value the
 * contract allows (`null` lab, an unscored session) or a value a real backend can still send wrong
 * (an unparseable date, a zone `Intl` rejects, a negative duration, a score outside 0–100) — the
 * screen renders a row either way rather than throwing inside a render.
 *
 * Formatting is deliberately absent: it needs the active language and lives in `sessionsFormat.ts`,
 * the same split `features/dashboard` uses.
 */
import type {
  AwaySegment,
  AwaySegmentDto,
  FactorObservation,
  FactorObservationDto,
  FactorSeverity,
  FactorSeverityDto,
  RecordingAvailability,
  RecordingAvailabilityDto,
  SafetyReasonDto,
  SafetyStatus,
  SafetyStatusDto,
  SessionDetail,
  SessionDetailDto,
  SessionFactor,
  SessionFactorDto,
  SessionListDto,
  SessionListItemDto,
  SessionPlayback,
  SessionPlaybackDto,
  SessionRow,
  SessionScore,
  SessionScoreDto,
  SessionsView,
  SessionStatus,
  SessionStatusDto,
} from './sessions.types';

const MIN_SCORE = 0;
const MAX_SCORE = 100;
const MIN_PERCENT = 0;
const MAX_PERCENT = 100;

const KNOWN_STATUSES: SessionStatusDto[] = [
  'completed',
  'abandoned',
  'interrupted',
  'paused',
  'inProgress',
  'expired',
];

const KNOWN_SAFETY: SafetyStatusDto[] = ['clean', 'flagged'];

const KNOWN_RECORDING: RecordingAvailabilityDto[] = ['available', 'none', 'expired'];

const KNOWN_SCORE_STATES: SessionScoreDto['state'][] = [
  'scored',
  'notScored',
  'pending',
  'recalculating',
  'limitedData',
  'interrupted',
  'paused',
];

const KNOWN_SEVERITIES: FactorSeverityDto[] = ['strong', 'needsAttention', 'weak'];

const KNOWN_SAFETY_REASONS: SafetyReasonDto[] = [
  'sashOpenOnExit',
  'sashAboveLine',
  'benchNotCleared',
  'deviceFault',
  'manualFlag',
];

function toDate(iso: string | null): Date | null {
  if (iso === null) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A zone `Intl` will actually accept.
 *
 * The list prints every row in the lab's zone, and `Intl.DateTimeFormat` throws a `RangeError` on
 * an id it does not know — inside a render, that is a blank screen rather than one odd cell. The
 * check is done once here so the formatter can assume its input is safe.
 */
function toTimeZone(value: string): string | null {
  if (value === '') return null;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
    return value;
  } catch {
    return null;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function toWholeSeconds(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
}

/**
 * A finite number at least `min`, or `min` itself.
 *
 * `Math.max(0, NaN)` is `NaN`, which reached the screen as "NaN of NaN possible pts" and as a
 * `NaN`-page pager. Everything numeric in this file goes through a guard for that reason.
 */
function atLeast(value: number, min: number): number {
  return Number.isFinite(value) ? Math.max(min, value) : min;
}

function toStatus(value: string): SessionStatus {
  return KNOWN_STATUSES.find((status) => status === value) ?? 'unknown';
}

function toSafetyStatus(value: string): SafetyStatus {
  return KNOWN_SAFETY.find((status) => status === value) ?? 'unknown';
}

function toRecording(value: string): RecordingAvailability {
  return KNOWN_RECORDING.find((state) => state === value) ?? 'unknown';
}

function toSafetyReason(value: string | null): SafetyReasonDto | null {
  if (value === null) return null;
  return KNOWN_SAFETY_REASONS.find((reason) => reason === value) ?? null;
}

/**
 * A score is a state plus, for exactly one state, a number.
 *
 * A `scored` state carrying no usable number at all — absent, `NaN`, infinite — is not a score, so
 * it degrades to `notScored`: painting a band colour over a value that does not exist would be a
 * claim about the session. A number that is merely out of range is a different case and is
 * clamped, because 140 means "the top band" far more plausibly than it means "unscored".
 */
export function toSessionScore(dto: SessionScoreDto | undefined): SessionScore {
  const state = KNOWN_SCORE_STATES.find((known) => known === dto?.state);
  if (state === undefined) return { state: 'unknown' };
  if (state !== 'scored') return { state };

  const raw = (dto as { value?: unknown } | undefined)?.value;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return { state: 'notScored' };
  return { state: 'scored', value: clamp(Math.round(raw), MIN_SCORE, MAX_SCORE) };
}

/**
 * The design shows a short device code; falling back to the id keeps the cell identifying rather
 * than blank when the backend omits it, and an absent `device` block yields an empty cell instead
 * of a thrown render.
 */
function deviceCode(device: SessionListItemDto['device']): string {
  const code = (device?.code ?? '').trim();
  return code === '' ? (device?.id ?? '') : code;
}

export function toSessionRow(dto: SessionListItemDto): SessionRow {
  const durationSeconds = toWholeSeconds(dto.durationSeconds);

  return {
    id: dto.id,
    startedAt: toDate(dto.startedAt),
    endedAt: toDate(dto.endedAt),
    durationSeconds,
    // The contract states away time is counted inside the duration; a payload that disagrees would
    // otherwise draw a marker past the end of the scrubber.
    awaySeconds: Math.min(durationSeconds, toWholeSeconds(dto.awaySeconds)),
    timeZone: toTimeZone(dto.timeZone),
    labId: dto.lab?.id ?? null,
    // An empty lab name is the same absence as `null`, and renders the same way. Read through
    // optional chaining rather than an equality check: a payload with `lab` *absent* rather than
    // `null` would otherwise throw inside a render, which is the failure this file exists to
    // prevent (HS-1440 review).
    labName: (dto.lab?.name ?? '').trim() === '' ? null : (dto.lab?.name ?? null),
    deviceId: dto.device?.id ?? '',
    deviceCode: deviceCode(dto.device),
    safetyStatus: toSafetyStatus(dto.safety?.status ?? ''),
    safetyReason: toSafetyReason(dto.safety?.reason ?? null),
    recording: toRecording(dto.recording),
    score: toSessionScore(dto.score),
    status: toStatus(dto.status),
  };
}

export function toSessionsView(dto: SessionListDto): SessionsView {
  const rows = dto.items.map(toSessionRow);
  const pageSize = atLeast(Math.round(dto.pageSize), 1);
  const totalCount = toWholeSeconds(dto.totalCount);

  return {
    rows,
    page: atLeast(Math.round(dto.page), 1),
    pageSize,
    totalCount,
    totalPages: Math.max(1, Math.ceil(totalCount / pageSize)),
    isEmpty: rows.length === 0,
    hasNoSessionsAtAll: toWholeSeconds(dto.totalUnfiltered) === 0,
    scoring: {
      activated: dto.scoring?.activated ?? true,
      sessionsRequired: toWholeSeconds(dto.scoring?.sessionsRequired ?? 0),
      hoursRequired: toWholeSeconds(dto.scoring?.hoursRequired ?? 0),
    },
  };
}

function toSeverity(value: string): FactorSeverity {
  return KNOWN_SEVERITIES.find((severity) => severity === value) ?? 'unknown';
}

/**
 * Observation keys are **not** validated against a list.
 *
 * The portal owns the copy but the backend owns the vocabulary, so a key the UI has no wording for
 * still reaches the card and renders as itself — visible, and the signal to add copy. Dropping it
 * would hide a real finding about the user's session.
 *
 * The **kind** is validated, and a member the contract gains later is read as `noted`, not as
 * `correct`. The two are not symmetric: filing an unrecognised finding under "Correct" would put a
 * future problem on the user's safety record as something they did right (HS-1440 review).
 */
function toObservation(dto: FactorObservationDto): FactorObservation {
  return {
    kind: dto.kind === 'correct' ? 'correct' : 'noted',
    key: dto.key,
    params: dto.params ?? {},
  };
}

export function toSessionFactor(dto: SessionFactorDto): SessionFactor {
  return {
    key: dto.key,
    weightPercent: clamp(atLeast(Math.round(dto.weightPercent), 0), MIN_PERCENT, MAX_PERCENT),
    score: clamp(atLeast(Math.round(dto.score), 0), MIN_SCORE, MAX_SCORE),
    // A zero maximum would make the bar divide by zero; one point is the smallest honest floor.
    // Points are then held under it, so a card cannot read "31 of 30".
    points: Math.min(atLeast(dto.points, 0), atLeast(dto.maxPoints, 1)),
    maxPoints: atLeast(dto.maxPoints, 1),
    severity: toSeverity(dto.severity),
    window: dto.window,
    observations: dto.observations.map(toObservation),
  };
}

function toAwaySegment(dto: AwaySegmentDto, durationSeconds: number): AwaySegment {
  const from = clamp(toWholeSeconds(dto.fromSeconds), 0, durationSeconds);
  const to = clamp(toWholeSeconds(dto.toSeconds), from, durationSeconds);
  return { fromSeconds: from, toSeconds: to };
}

export function toSessionDetail(dto: SessionDetailDto): SessionDetail {
  const row = toSessionRow(dto);

  return {
    ...row,
    formulaVersion: dto.formulaVersion,
    sashClosedOnExit: dto.sashClosedOnExit,
    overrides:
      dto.overrides === null
        ? null
        : {
            count: toWholeSeconds(dto.overrides.count),
            totalSeconds: toWholeSeconds(dto.overrides.totalSeconds),
          },
    // Clamped to the session's own length: a segment past the end would draw off the scrubber.
    awaySegments: dto.awaySegments
      .map((segment) => toAwaySegment(segment, row.durationSeconds))
      .filter((segment) => segment.toSeconds > segment.fromSeconds),
    factors: dto.factors.map(toSessionFactor),
  };
}

/**
 * Playback drops `expiresAt` on the way in: the component has no use for it, and a credential's
 * lifetime is the query's concern (its `staleTime`), not the player's.
 */
export function toSessionPlayback(dto: SessionPlaybackDto): SessionPlayback {
  if (dto.kind === 'live') return { kind: 'live', wsUrl: dto.wsUrl, token: dto.token };
  if (dto.kind === 'recording') {
    return {
      kind: 'recording',
      wsUrl: dto.wsUrl,
      token: dto.token,
      recordingUrl: dto.recordingUrl,
      durationSeconds: toWholeSeconds(dto.durationSeconds),
    };
  }
  return { kind: 'none', reason: dto.reason === 'expired' ? 'expired' : 'notRecorded' };
}
